import { Edit } from '@mui/icons-material';
import { Box, Card, CardContent, IconButton, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type {
    EAvailabilityInputAs,
    EAvailabilitySelectionType,
    IAvailabilityWeekDTO,
} from '@sharedInterfaces/IAvailabilityMonth';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import AvailabilityMonthDialog from '@src/App/Components/Dialogs/AvailabilityMonthDialog/AvailabilityMonthDialog';
import { useDialog } from '@src/App/Components/Dialogs/DialogManager';
import AvailabilityMonth from '@src/Objects/AvailabilityMonth';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';

interface MonthBoxProps {
    month: AvailabilityMonth;
    setAvailability: (
        id: string,
        weekArray: IAvailabilityWeekDTO[],
        selectionType: EAvailabilitySelectionType,
        inputAs: EAvailabilityInputAs,
    ) => void;
    setMonths: (objs: AvailabilityMonth[]) => void;
}
/**
 * Renders a MonthBox component.
 *
 * @param {MonthBoxProps} props - The MonthBox props.
 * @returns {JSX.Element} - The rendered MonthBox component.
 */
export function MonthBox(props: MonthBoxProps) {
    const theme = useTheme();
    const { openDialog } = useDialog();
    const month = props.month;
    const lang = useSelector((state: AppState) => state.employee.language);
    const offline = useSelector((state: AppState) => state.client.offline);
    const langStrings = getLanguageTexts(lang).availability;

    const availability =
        100 -
        Math.round(((month.plannedDayCount + month.absenceDayCount + month.holidayCount) / month.workDayCount) * 100);

    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                background: 'transparent',
                '&:hover': {
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    boxShadow: theme.shadows[2],
                },
            }}
        >
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        {month.LocalTitle}
                    </Typography>
                    <IconButton
                        disabled={offline}
                        size="small"
                        onClick={() => {
                            openDialog(
                                <AvailabilityMonthDialog
                                    id={'AvailabilityMonthDialog'}
                                    availabilityMonth={month}
                                    onEdit={(weekArray, type, inputType) => {
                                        props.setAvailability(month.id, weekArray, type, inputType);
                                        AvailabilityMonth.getAvailabilityMonths((newMonths) => {
                                            props.setMonths(newMonths);
                                        });
                                    }}
                                    resolve={() => {}}
                                />,
                            );
                        }}
                        aria-label={`${month.LocalTitle} bearbeiten`}
                        sx={{
                            color: theme.palette.primary.main,
                            '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                            },
                        }}
                    >
                        <Edit fontSize="small" />
                    </IconButton>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            {langStrings.workdays}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {month.workDayCount - month.holidayCount}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            {langStrings.plannedDays}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {Math.round(month.plannedDayCount * 10) / 10}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            {langStrings.absenceDays}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {month.absenceDayCount}
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }} title={langStrings.onlyOnWorkday}>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            {langStrings.holidays}*
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {month.holidayCount}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            pt: 1,
                            borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        }}
                    >
                        <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                            {langStrings.oftenUsed.availability}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 700,
                                color:
                                    availability >= 80
                                        ? theme.palette.success.main
                                        : availability >= 60
                                          ? theme.palette.warning.main
                                          : theme.palette.error.main,
                            }}
                        >
                            {availability}%
                        </Typography>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
}
